import axios from "axios";
import UserService from "rc-app/services/UserService";

/* ── Guided Room API client ────────────────────────────────────────────
   Backs the guided tour room feature — see
   docs/product specs/GUIDED_TOUR_ROOM_SPEC.md.

   Backend live on Nivedita's tunnel as of 2026-08-04; contract re-verified
   against her local server 2026-08-19. Eighteen endpoints across two
   namespaces:
     /api/v1/realestate/actions/guided_room/...   → writes
     /api/v1/realestate/views/guided_room/...     → reads

   ── PARTICIPANT CREDENTIALS (the core of the auth model) ──
   Room participants are NOT Keycloak users. Each one gets a
   `session_id` + `session_secret` pair which is their credential for the
   room:
     - create()            → host_session_id / host_session_secret
     - verifyGuidedRoomOtp → session_id / session_secret (verified lead)
     - guestJoinGuidedRoom → session_id / session_secret (guest)
   Store the pair on join and pass it to getRoomRtcToken() and
   issueGuestPass(). The backend derives identity and privileges from it,
   so nothing security-relevant is ever taken from the client.

   ── AUTH ──
   These endpoints are PUBLIC (excluded from Keycloak in the auth
   middleware — the global security in the OpenAPI spec was misleading):
     request_otp · verify_otp · guest_pass · guest/join · leave · rtc_token
     ws_token · identify · views/join/{join_code}
   Everything else needs a staff Keycloak token. Verified live 2026-08-19:
   rtc_token and ws_token answer 403 "Not an admitted participant of this
   room." rather than 401, i.e. they authorise off the credential and not
   off Keycloak, exactly as designed.

   ── STILL DEFERRED ──
   Recording and auto-recap are media-vendor features; the end flow has a
   hook stubbed backend-side. Piloting without both.

   ── NOTE ──
   Participant sessions are created SERVER-side by verify_otp / guest_join.
   Do NOT call /vr/session/start for room participants — it duplicates.

   ── TWO TOKENS, NOT ONE ──
   A room needs both, fetched with the same credential:
     rtc_token → Agora, the voice channel.
     ws_token  → the VR experience server, which is what keeps everyone's
                 camera on the same spot in the walkthrough.
   Real tokens as of 2026-08-19 (rtc_token was a stub until then). The
   200 body is untyped in the OpenAPI spec, so read the field names off a
   live response before wiring the Agora client — see getRoomRtcToken.

   Media vendor: Agora (already a dependency — agora-react-uikit).
*/

const ACTIONS = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/actions/guided_room`;
const VIEWS = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/views/guided_room`;

const authHeaders = () => ({
  Authorization: `Bearer ${UserService.getToken()}`,
  "ngrok-skip-browser-warning": "disabled",
  "Content-Type": "application/json",
});

const publicHeaders = () => ({
  "ngrok-skip-browser-warning": "disabled",
  "Content-Type": "application/json",
});

/* Join-flow endpoints are used by people who may or may not be logged in:
   a prospect opening the link cold has no token, but an agent testing
   their own room does. Send the bearer only when we actually have one —
   the backend can then treat it as identity if it wants, and anonymous
   joins still work. (The OpenAPI spec declares keyCloakOAuth globally
   with no per-endpoint override, which is almost certainly a spec
   artefact rather than intent — see gap 2 above.) */
const maybeAuthHeaders = () => {
  const token = UserService.isLoggedIn() ? UserService.getToken() : null;
  return token ? authHeaders() : publicHeaders();
};

/* ── Host: room lifecycle ───────────────────────────────────────────── */

/**
 * Create a guided tour room. Host + agency are derived from the JWT —
 * never send a client-supplied host id.
 * Also returns the HOST's own participant credential — store it, the host
 * needs it to fetch its RTC token exactly like everyone else.
 *
 * CONTRACT CHANGED 2026-08-19: `invitees` is a REQUIRED top-level array of
 * {id, kind} and `primary_lead_id` no longer exists. `kind` is what routes
 * a participant — lead → OTP path and auto-admitted, agent/asa → Keycloak
 * path and no lobby. `Invitee` carries no name, so names are mirrored into
 * `settings.invitees` by the caller for display purposes only.
 * @param {{ project_id: string,
 *           invitees: Array<{ id: string, kind: "lead"|"agent"|"asa" }>,
 *           experience_id?: string,
 *           instant?: boolean, scheduled_at?: string|null, settings?: object }} payload
 * @returns {{ room_id: string, join_code: string, status: string,
 *             host_session_id: string, host_session_secret: string }}
 */
export const createGuidedRoom = async (payload) => {
  const r = await axios.post(ACTIONS, payload, { headers: authHeaders() });
  return r.data;
};

/**
 * Update a room — state transitions and settings both go through here.
 * `action` drives the state machine (open lobby / start / cancel etc.);
 * `scheduled_at` reschedules a SCHEDULED room.
 * @param {string} roomId
 * @param {{ action?: string, settings?: object, scheduled_at?: string }} payload
 */
export const patchGuidedRoom = async (roomId, payload) => {
  const r = await axios.patch(`${ACTIONS}/${roomId}`, payload, {
    headers: authHeaders(),
  });
  return r.data;
};

/**
 * End the room. No body — the room is identified by the path.
 * Finalises the session for every participant.
 * @param {string} roomId
 */
export const endGuidedRoom = async (roomId) => {
  const r = await axios.post(`${ACTIONS}/${roomId}/end`, undefined, {
    headers: authHeaders(),
  });
  return r.data;
};

/* ── Host: recording ────────────────────────────────────────────────────
   re-admin-server recording_routers.py. Host-only, Keycloak. Recording
   auto-starts only for a SCHEDULED room's `start`; an instant room (every
   room the project page creates) is born LIVE and needs these. Stop hands
   transcription + analysis to a background task — poll the list. */

/**
 * @param {string} roomId
 * @returns {{ success: boolean, room_id: string, recording_id: string, state: string }}
 */
export const startGuidedRoomRecording = async (roomId) => {
  const r = await axios.post(`${ACTIONS}/${roomId}/recording/start`, undefined, {
    headers: authHeaders(),
  });
  return r.data;
};

/**
 * @param {string} roomId
 * @returns {{ success: boolean, room_id: string, recording_id: string, state: string, audio_url?: string }}
 */
export const stopGuidedRoomRecording = async (roomId) => {
  const r = await axios.post(`${ACTIONS}/${roomId}/recording/stop`, undefined, {
    headers: authHeaders(),
  });
  return r.data;
};

/**
 * Newest first, at most 20, no transcripts (the list is for drawing rows).
 * @param {string} roomId
 * @returns {Promise<Array<{ recording_id: string, state: string, error?: string,
 *   started_at?: string, stopped_at?: string, duration_seconds?: number,
 *   audio_url?: string, sentiment?: string, sentiment_score?: number }>>}
 */
export const listGuidedRoomRecordings = async (roomId) => {
  const r = await axios.get(`${VIEWS}/${roomId}/recordings`, {
    headers: authHeaders(),
  });
  return r.data?.recordings || [];
};

/**
 * One recording with its transcript and analysis. `refresh` re-asks Agora
 * where the file went when the upload finished after stop returned.
 * @param {string} recordingId
 * @param {boolean} [refresh=false]
 */
export const getGuidedRoomRecording = async (recordingId, refresh = false) => {
  const r = await axios.get(`${VIEWS}/recording/${recordingId}`, {
    headers: authHeaders(),
    params: refresh ? { refresh: true } : undefined,
  });
  return r.data;
};

/* ── Host: lobby + participants ─────────────────────────────────────── */

/**
 * Admit or reject someone waiting in the lobby. Host-only (enforced
 * backend-side).
 * @param {string} roomId
 * @param {string} sessionId  participant's session id (from the lobby list)
 * @param {"admit"|"reject"} [decision="admit"]
 */
export const admitToGuidedRoom = async (roomId, sessionId, decision = "admit") => {
  const r = await axios.post(
    `${ACTIONS}/${roomId}/admit`,
    { session_id: sessionId, decision },
    { headers: authHeaders() },
  );
  return r.data;
};

/**
 * Mute / unmute / remove a participant mid-session. Host-only.
 * @param {string} roomId
 * @param {string} sessionId
 * @param {string} action  e.g. "mute" | "unmute" | "remove"
 */
export const manageParticipant = async (roomId, sessionId, action) => {
  const r = await axios.post(
    `${ACTIONS}/${roomId}/participant/${sessionId}/manage`,
    { action },
    { headers: authHeaders() },
  );
  return r.data;
};

/**
 * Leave the room. Used by host and participants alike; the participant is
 * identified by session_id rather than by token, so the caller must know
 * its own session id.
 * @param {string} roomId
 * @param {string} sessionId
 */
export const leaveGuidedRoom = async (roomId, sessionId) => {
  const r = await axios.post(
    `${ACTIONS}/${roomId}/leave`,
    { session_id: sessionId },
    { headers: maybeAuthHeaders() },
  );
  return r.data;
};

/* ── Guest passes (family / household) ──────────────────────────────── */

/**
 * Get this lead's family pass for the room. ONE link, shared with up to
 * four people — not four separate links.
 *
 * IDEMPOTENT: calling it again returns the SAME token plus the remaining
 * count, it does not mint another. A lead is inside a live tour, usually
 * on a phone; minting four tokens and composing four WhatsApp messages
 * mid-walkthrough is friction they'd route around. One link into the
 * family group chat is what a person actually does.
 *
 * The cap is on TOTAL joins, enforced server-side, and the decrement must
 * be atomic — two people tapping at once with one seat left is the only
 * way the limit gets exceeded. A rejoin on an existing credential must
 * NOT consume a seat: a dropped phone reconnecting would otherwise cost
 * the family a slot and can lock a household out of its own tour.
 *
 * The issuer is derived from the credential, NOT from anything the client
 * claims — so a forwarded pass can't be used to mint passes as someone
 * else. Both the host and a verified lead can call this; each just passes
 * their own credential.
 * @param {string} roomId
 * @param {{ session_id: string, session_secret: string }} credential
 * @returns {Promise<{ guest_code?: string, guest_token: string, join_path?: string, used?: number, remaining?: number, max?: number, expires_at?: string }>}
 *
 * `guest_code` is the short, URL-safe replacement for the JWT — asked for
 * in GUIDED_ROOM_FAMILY_PASS_CODE.md, not shipped as of 8 September. The
 * guest screen already prefers it when present, so the link shortens on
 * the backend deploy with no release here.
 */
export const issueGuestPass = async (roomId, credential) => {
  const r = await axios.post(
    `${ACTIONS}/${roomId}/guest_pass`,
    { session_id: credential?.session_id, session_secret: credential?.session_secret },
    { headers: publicHeaders() },
  );
  return r.data;
};

/**
 * Room-scoped RTC token. The channel comes from room_id and the role from
 * the participant's presence record (guests → subscriber, host/lead →
 * publisher); anything the client sends is ignored. Requires an ADMITTED
 * session, so you can't pull a token for a room you were never let into.
 *
 * Mints a REAL Agora token as of 2026-08-19. The 200 body is declared
 * untyped in the OpenAPI spec, so whether it carries the app id, channel
 * and uid alongside the token — or only the token — has to be read off a
 * live response before the Agora client can be wired. Note we have no
 * REACT_APP_AGORA_APP_ID today: Pages/World reads the app id out of the
 * KEYCLOAK url, which is leftover prototype junk. If the response doesn't
 * carry the app id, that env var has to be added to BOTH .env and the
 * webpack DefinePlugin allow-list.
 *
 * Doubles as the admission probe on the join page: it requires an ADMITTED
 * session, so a 200 means the host let you in.
 * @param {string} roomId
 * @param {{ session_id: string, session_secret: string }} credential
 */
export const getRoomRtcToken = async (roomId, credential) => {
  const r = await axios.post(
    `${ACTIONS}/${roomId}/rtc_token`,
    { session_id: credential?.session_id, session_secret: credential?.session_secret },
    { headers: publicHeaders() },
  );
  return r.data;
};

/**
 * Room-scoped VR sync token — the credential for the experience server's
 * websocket, which is what makes a guided tour guided: the host moves and
 * everyone else's camera follows.
 *
 * Same auth model and same request body as getRoomRtcToken: public
 * endpoint, ADMITTED session required, identity and privilege derived
 * server-side from the credential. A guest can only ever follow, never
 * drive, and that's decided by the backend rather than by the client.
 *
 * Fetch it alongside the RTC token once admitted — a room needs both.
 * The 200 body is untyped in the spec; confirm the field names (token,
 * and whatever socket url / channel accompanies it) against a live
 * response before wiring the socket.
 * @param {string} roomId
 * @param {{ session_id: string, session_secret: string }} credential
 */
export const getRoomWsToken = async (roomId, credential) => {
  const r = await axios.post(
    `${ACTIONS}/${roomId}/ws_token`,
    { session_id: credential?.session_id, session_secret: credential?.session_secret },
    { headers: publicHeaders() },
  );
  return r.data;
};

/**
 * Guest join — first name only, no mobile, no OTP. Public.
 * Returns the guest's participant credential — store it the same way as a
 * lead's. A guest's role resolves to subscriber, so their RTC token can
 * only ever subscribe.
 *
 * AUTO-ADMITS. A pass holder was vouched for by a verified lead, so they
 * are not queued — see GUIDED_ROOM_JOIN_OTP_ALIGNMENT.md §A.6, which
 * reverses the original "always lobby" rule.
 *
 * Expected failure worth handling distinctly: the pass is exhausted (all
 * four joins used). The page shows a dedicated state rather than a form
 * that fails on submit — 409 or an `exhausted` flag, whichever you prefer.
 * @param {string} guestToken  from the /pass/<token> link
 * @param {string} firstName
 * @returns {{ session_id: string, session_secret: string, room_id?: string }}
 */
export const guestJoinGuidedRoom = async (guestToken, firstName) => {
  const r = await axios.post(
    `${ACTIONS}/guest/${encodeURIComponent(guestToken)}/join`,
    { first_name: firstName },
    { headers: publicHeaders() },
  );
  return r.data;
};

/* ── Lead join (OTP) ────────────────────────────────────────────────── */

/**
 * Is this mobile already on the agency's book? Asked BEFORE the OTP so the
 * page knows whether to collect a name and email first, and so the step
 * counter renders the right length ("of 3" vs "of 4").
 *
 * MUST return a bare boolean and nothing else. The caller is an unverified
 * stranger on a public page — returning the stored contact's name, or even
 * which case they are (invited vs merely known), would turn a join code
 * into a way to probe the agency's book number by number. Rate-limit it.
 *
 * SHIPPED as of 2026-08-19 — see GUIDED_ROOM_JOIN_OTP_ALIGNMENT.md §A.1.
 * The join page still treats a failure as "we don't know" and falls back
 * to the old order (send the OTP, collect details afterwards if verify_otp
 * asks for them), which is the right behaviour to keep for a timeout.
 * @param {string} joinCode
 * @param {{ mobile_number: string, country_code?: string }} payload
 * @returns {Promise<{ known: boolean }>}
 */
export const identifyGuidedRoomMobile = async (joinCode, payload) => {
  const r = await axios.post(
    `${ACTIONS}/${encodeURIComponent(joinCode)}/identify`,
    payload,
    { headers: publicHeaders() },
  );
  return r.data;
};

/**
 * Send the join OTP. Keyed on join_code, so the caller needs only the
 * link. Name fields are optional — pass them when the landing form
 * collects them so a newly created lead isn't nameless.
 * @param {string} joinCode
 * @param {{ mobile_number: string, country_code?: string, first_name?: string, last_name?: string }} payload
 */
export const requestGuidedRoomOtp = async (joinCode, payload) => {
  const r = await axios.post(
    `${ACTIONS}/${encodeURIComponent(joinCode)}/request_otp`,
    payload,
    { headers: publicHeaders() },
  );
  return r.data;
};

/**
 * Verify the OTP and join as a verified lead. Backend is expected to
 * match-or-create the re_customer with source = guided_room (unconfirmed
 * — see gap 2), and to place the participant in the lobby as WAITING.
 * @param {string} joinCode
 * Returns the participant credential — STORE IT. It's what the lead uses
 * for getRoomRtcToken() and issueGuestPass(); there's no other way back
 * to their identity, and it's not recoverable if lost.
 * @param {{ mobile_number: string, otp: string, country_code?: string }} payload
 * @returns {{ session_id: string, session_secret: string, room_id?: string, status?: string }}
 */
export const verifyGuidedRoomOtp = async (joinCode, payload) => {
  const r = await axios.post(
    `${ACTIONS}/${encodeURIComponent(joinCode)}/verify_otp`,
    payload,
    { headers: publicHeaders() },
  );
  return r.data;
};

/**
 * Complete a NEW lead's profile after OTP, before they enter the lobby.
 *
 * Only reached when verify_otp reports the mobile wasn't already a lead of
 * this agency (`needs_profile`). The lead row is created by verify_otp with
 * source = guided_tour; this fills in the name and email the host needs in
 * order to decide whether to admit a stranger.
 *
 * Identity comes from the credential, never from the body — otherwise
 * anyone could rewrite any participant's profile.
 *
 * STILL NOT SHIPPED — re-checked 2026-08-19, the route is absent from the
 * backend's OpenAPI and this 404s. Until it lands the join page skips the
 * step and drops the participant straight into the lobby, which is exactly
 * today's behaviour. The only remaining hole in the join flow.
 * @param {string} joinCode
 * @param {{ session_id: string, session_secret: string }} credential
 * @param {{ first_name: string, last_name?: string, email?: string }} profile
 */
export const submitGuidedRoomProfile = async (joinCode, credential, profile) => {
  const r = await axios.post(
    `${ACTIONS}/${encodeURIComponent(joinCode)}/profile`,
    {
      session_id: credential?.session_id,
      session_secret: credential?.session_secret,
      ...profile,
    },
    { headers: publicHeaders() },
  );
  return r.data;
};

/* ── Reads ──────────────────────────────────────────────────────────── */

/**
 * PUBLIC room preview by join code. Backs the branded landing page — the
 * buyer sees the project, the agency and who's hosting BEFORE being asked
 * for their mobile number.
 *
 * Deliberately whitelisted: no participants, no lead data, no room
 * internals. Same shape and reasoning as /api/v1/public/projects/{id}.
 *
 * SHIPPED as of 2026-08-19 — an unknown code now answers 404 with
 * "No guided room for this join code." rather than the endpoint itself
 * being absent. The landing page still degrades to a generic version on
 * failure, which stays the right behaviour for a dead or expired link.
 * @param {string} joinCode
 * @returns {Promise<{ project_name: string, agency_name: string, agency_logo_url?: string,
 *             host_first_name?: string, status?: string, scheduled_at?: string,
 *             invited_by_name?: string }>}
 */
/*
 * ── WHO SENT THE FAMILY LINK ──
 * The family link now arrives at /visit/<join_code>?g=<guest_code>, so a
 * relative's first screen can name the home and show the consent notice
 * before they tap anything. The one thing it still cannot say is WHO
 * invited them: the page renders "Arjun invited you" when the preview
 * carries `invited_by_name`, and the backend hard-codes that field to None
 * with a comment reading "populated only for ?g=<guest_token> links
 * (future)" — because nothing ever sent it the guest code to look up.
 *
 * So the code is passed along. FastAPI ignores query params it doesn't
 * declare, so this is inert until the backend reads it (addendum item 30),
 * and the name appears the moment it does, with no release here. Defaults
 * to the page's own `g` so no caller has to change.
 */
export const getGuidedRoomPreview = async (
  joinCode,
  guestCode = (typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("g") || ""
    : ""),
) => {
  const r = await axios.get(`${VIEWS}/join/${encodeURIComponent(joinCode)}`, {
    headers: publicHeaders(),
    params: guestCode ? { g: guestCode } : undefined,
  });
  return r.data;
};

/**
 * Full room document — state, settings, project/experience, participants.
 * @param {string} roomId
 */
export const getGuidedRoom = async (roomId) => {
  const r = await axios.get(`${VIEWS}/${roomId}`, { headers: maybeAuthHeaders() });
  return r.data;
};

/**
 * Lobby queue — everyone currently WAITING plus those already admitted.
 * Polled by the host's admit UI and by the joiner's waiting screen.
 * @param {string} roomId
 */
export const getGuidedRoomLobby = async (roomId) => {
  const r = await axios.get(`${VIEWS}/${roomId}/lobby`, {
    headers: maybeAuthHeaders(),
  });
  return r.data;
};

/**
 * The admitted roster, for a PARTICIPANT — "who is in the room with me".
 *
 * The lobby above is host-only (`virtual_tour:read` + an is-host assertion),
 * so a buyer polling it gets 401 and an empty list, and their People tab
 * reported that emptiness as a fact about the room. This is the same
 * question asked with the credential they actually hold — the gate used by
 * rtc_token and the chat write.
 *
 * Deliberately NOT the lobby: it returns only ADMITTED rows, never the
 * WAITING queue (that is the host's gatekeeping view), and it is
 * PII-stripped — no lead_id, no lead_source, no new/known classification.
 * A buyer sees who is present and their role, and nothing about anyone's
 * standing as a lead.
 *
 * Shipped 2026-09-02, reachable from 2026-09-08 (it needed adding to the
 * middleware's public allow-list; `openapi_extra={"security": []}` marks an
 * endpoint public in the docs without making it so).
 *
 * @param {string} roomId
 * @param {{ session_id: string, session_secret: string }} credential
 * @returns {Promise<{ room_id: string, admitted: Array<{ session_id: string,
 *            role: string, display_name?: string, invited_by?: string,
 *            lobby_status?: string }> }>}
 */
export const getGuidedRoomRoster = async (roomId, credential) => {
  const r = await axios.post(
    `${ACTIONS}/${roomId}/roster`,
    { session_id: credential?.session_id, session_secret: credential?.session_secret },
    { headers: publicHeaders() },
  );
  return r.data;
};

/**
 * Rooms hosted by a given user — backs the host's "My Rooms" list.
 * @param {string} hostId
 */
export const listHostGuidedRooms = async (hostId) => {
  const r = await axios.get(`${VIEWS}/host/${hostId}`, { headers: authHeaders() });
  return r.data;
};

/**
 * Record one chat message so it outlives the room.
 *
 * Chat itself does NOT go through here. Messages travel peer-to-peer over
 * the Agora data stream (utils/guidedRoomChat.ts) and appear instantly;
 * this is a second, parallel write whose only job is that the message
 * still exists after the tour ends — where it joins the voice transcript
 * and the analysis of what the buyer actually wanted.
 *
 * So the caller must treat a failure here as nothing: the message is
 * already in everyone's room. Never block the composer on it, never
 * surface an error for it, never retry it into a queue. Persistence is a
 * by-product.
 *
 * Same auth as getRoomRtcToken — the participant credential, because a
 * buyer has no account. The backend takes the sender's role and display
 * name from their presence record and ignores anything the body claims
 * about either.
 *
 * @param {string} roomId
 * @param {{ session_id: string, session_secret: string }} credential
 * @param {{ text: string, sent_at_ms: number, display_name?: string }} message
 */
/**
 * The chat that already happened, for a PARTICIPANT.
 *
 * Live chat rides Agora's data stream and exists only in the browser that
 * received it, so a buyer who refreshes — routine on a phone when a
 * backgrounded tab reloads — came back to an empty log mid-tour while the
 * conversation sat safely in the database. The host's own read of the same
 * data is staff-only, so a buyer could not fetch the history of a
 * conversation they were in.
 *
 * Same credential gate as the write and the roster. Shipped 2026-09-08.
 *
 * @param {string} roomId
 * @param {{ session_id: string, session_secret: string }} credential
 * @param {number} [limit] newest N, default 100
 * @returns {Promise<{ messages: Array<{ session_id: string, display_name?: string,
 *            role: string, text: string, sent_at_ms: number }> }>}
 */
export const getGuidedRoomMessages = async (roomId, credential, limit = 100) => {
  const r = await axios.post(
    `${ACTIONS}/${roomId}/messages`,
    {
      session_id: credential?.session_id,
      session_secret: credential?.session_secret,
      limit,
    },
    { headers: publicHeaders() },
  );
  return r.data;
};

export const recordGuidedRoomMessage = async (roomId, credential, message) => {
  const r = await axios.post(
    `${ACTIONS}/${roomId}/message`,
    {
      session_id: credential?.session_id,
      session_secret: credential?.session_secret,
      text: message?.text,
      sent_at_ms: message?.sent_at_ms || 0,
      display_name: message?.display_name || "",
    },
    { headers: publicHeaders() },
  );
  return r.data;
};
